import { useRef } from "react";
import { motion, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { ArrowUpRight, Dumbbell, Smartphone } from "lucide-react";
import { SITE, whatsappUrl } from "@/config/site";
import { AccessLink } from "@/components/AccessLink";
import { WhatsAppIcon } from "@/components/icons";
import { cn } from "@/lib/utils";

const EASE = [0.22, 1, 0.36, 1];

function RevealLine({ index, children }) {
  return (
    <span className="-mb-[0.12em] block overflow-hidden pb-[0.12em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "112%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.95, ease: EASE, delay: 0.15 + index * 0.13 }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function FloatChip({ className, label, delay = 0 }) {
  return (
    <div
      className={cn(
        "animate-float absolute rounded-full border border-white/10 bg-[#0d121c]/85 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-200 shadow-lg backdrop-blur",
        className,
      )}
      style={{ animationDelay: `${delay}s` }}
    >
      {label}
    </div>
  );
}

function KineticVisual() {
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[320px] sm:max-w-[440px]" data-testid="hero-kinetic-visual">
      <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,rgba(53,130,124,0.18),transparent_65%)]" />
      <div className="animate-spin-slow absolute inset-0 rounded-full border border-dashed border-brand-teal/40" />
      <div className="animate-spin-reverse absolute inset-[14%] rounded-full border border-brand-blue/25 border-t-brand-blue/70" />
      <div className="absolute inset-[28%] rounded-full border border-white/10" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex h-28 w-28 items-center justify-center rounded-full border border-brand-teal/40 bg-[#0d121c]/80 shadow-[0_0_60px_rgba(53,130,124,0.25)] backdrop-blur">
          <Dumbbell className="h-12 w-12 text-brand-blue" strokeWidth={1.5} />
        </div>
      </div>
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" fill="none" aria-hidden="true">
        <path
          d="M14 78 A 46 46 0 0 1 86 22"
          stroke="url(#fw-grad)"
          strokeWidth="0.7"
          strokeLinecap="round"
          strokeDasharray="2 3"
        />
        <defs>
          <linearGradient id="fw-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#06B6D4" stopOpacity="0" />
            <stop offset="1" stopColor="#22D3EE" />
          </linearGradient>
        </defs>
      </svg>
      <FloatChip className="left-[0%] top-[12%]" label="Emagrecimento" />
      <FloatChip className="right-[0%] top-[40%]" label="Hipertrofia" delay={1.4} />
      <FloatChip className="right-[4%] bottom-[10%]" label="Reabilitação" delay={0.8} />
      <FloatChip className="left-[6%] bottom-[6%]" label="60+" delay={2} />
      <FloatChip className="left-[26%] top-[0%]" label="Qualidade de vida" delay={2.6} />
    </div>
  );
}

export default function HeroKinetic() {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const reduce = useReducedMotion();
  const wa = whatsappUrl();

  const onMouseMove = (e) => {
    if (reduce) return;
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set(((e.clientX - rect.left) / rect.width - 0.5) * 18);
    my.set(((e.clientY - rect.top) / rect.height - 0.5) * 14);
  };

  return (
    <section
      id="inicio"
      ref={sectionRef}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pb-20 pt-28"
    >
      <div className="pointer-events-none absolute -top-40 right-[8%] h-[420px] w-[420px] rounded-full bg-brand-teal/20 blur-[130px]" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-[360px] w-[360px] rounded-full bg-brand-teal/15 blur-[120px]" aria-hidden="true" />

      <div className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.12fr_0.88fr]">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.05 }}
            className="font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-blue sm:text-xs"
            data-testid="hero-trainer-name"
          >
            {SITE.trainerName} | {SITE.trainerRole}
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-5 font-display text-[clamp(2.6rem,6.2vw,5.2rem)] font-extrabold uppercase leading-[1.04] tracking-tight"
          >
            <RevealLine index={0}>
              <span className="text-white">Movimento</span>
            </RevealLine>
            <RevealLine index={1}>
              <span className="text-outline">Gera</span>
            </RevealLine>
            <RevealLine index={2}>
              <span className="text-gradient-cyan">Mudança.</span>
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.62 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subtitle"
          >
            Treinamento personalizado para transformar sua rotina e ajudar você a alcançar
            seus objetivos com orientação e constância.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.74 }}
            className="mt-9 flex flex-col gap-3.5 sm:flex-row sm:items-center"
          >
            <AccessLink
              href={wa}
              external
              testId="hero-cta-start-whatsapp"
              badgeClassName="ml-3 border-slate-900/20 bg-slate-950/10 text-slate-900"
              className="inline-flex whitespace-nowrap items-center justify-center rounded-xl bg-gradient-to-r from-brand-tealight to-brand-blue px-7 py-3.5 text-base font-bold text-slate-950 shadow-[0_0_24px_rgba(94,155,150,0.35)] transition-all hover:scale-[1.02] hover:shadow-[0_0_36px_rgba(94,155,150,0.55)] active:scale-[0.98] aria-disabled:opacity-60 aria-disabled:shadow-none aria-disabled:hover:scale-100"
            >
              <WhatsAppIcon className="mr-2.5 h-5 w-5" />
              Quero começar
            </AccessLink>

            <AccessLink
              href={SITE.links.appTreino}
              external
              testId="hero-cta-access-app"
              badgeClassName="ml-3"
              className="inline-flex whitespace-nowrap items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-3.5 text-base font-semibold text-white transition-all hover:scale-[1.02] hover:border-brand-teal/60 hover:bg-white/10 hover:text-brand-blue active:scale-[0.98] aria-disabled:opacity-60 aria-disabled:hover:scale-100"
            >
              <Smartphone className="mr-2.5 h-5 w-5" />
              Acessar meu treino
              <ArrowUpRight className="ml-2 h-4 w-4 opacity-60" />
            </AccessLink>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.86 }}
            className="mt-10 flex flex-wrap items-center gap-2"
            data-testid="hero-pillars"
          >
            <span className="rounded-full border border-brand-teal/40 bg-brand-teal/15 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-blue" data-testid="hero-cref">
              {SITE.cref}
            </span>
            {["Emagrecimento", "Hipertrofia", "Reabilitação", "60+", "Qualidade de vida"].map((p) => (
              <span
                key={p}
                className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300"
              >
                {p}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ x: sx, y: sy }} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: EASE, delay: 0.4 }}>
          <KineticVisual />
        </motion.div>
      </div>
    </section>
  );
}
